/**
 * Page to display search results from Home page
 * /search
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import {
  Footer,
  KonektoNavbar,
  Loader,
  PaginationList,
  Product,
  SearchFilters,
} from '../../components'
import '../../../src/App.scss'
import './Categories.scss'
import { useLocation, useNavigate } from 'react-router-dom'
import { hasValue } from '../../helpers'
import { productService } from '../../services/productService'
import { BiCategoryAlt } from 'react-icons/bi'
import { IoChevronForward } from 'react-icons/io5'

import { useSelector } from 'react-redux'

export default function Categories() {
  const navigate = useNavigate()
  const location = useLocation()

  const categories = useSelector(state => state.category.list)

  const queryParams = new URLSearchParams(location.search)

  const category = queryParams.get('c') || ''
  const subcategory = queryParams.get('s') || ''
  const currentPage = queryParams.get('page') || 1
  const city = queryParams.get('grad') || ''

  const [products, setProducts] = useState([])

  const [loader, setLoader] = useState(false)
  const [totalPages, setTotalPages] = useState(1)

  const onCategoryClick = (categoryId, subcategoryId) => {
    queryParams.set('c', categoryId)

    if (subcategoryId) {
      queryParams.set('s', subcategoryId)
    }

    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 70, behavior: 'smooth' })
  }

  const onProductCardClick = product => {
    navigate(`/firma/${product.company_id}?p=${product.id}`)
  }

  const handlePageChange = newPage => {
    queryParams.set('page', newPage)
    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 70, behavior: 'smooth' })
  }

  const resetQueryParams = () => {
    queryParams.delete('c')
    queryParams.delete('s')
    queryParams.delete('page')
    queryParams.delete('grad')
    navigate(`?${queryParams.toString()}`)
  }

  const selectedCategory = useMemo(() => {
    return categories?.find(c => c.id == category)
  }, [category, categories])

  const selectedSubcategory = useMemo(() => {
    return selectedCategory?.children?.find(c => c.id == subcategory)
  }, [category, subcategory, selectedCategory])

  useEffect(() => {
    const getProductsByCategory = async () => {
      setLoader(true)

      const response = await productService.getProductsByCategory({
        category,
        subcategory,
        city,
        page: currentPage,
      })
      if (response.isOk && response.data) {
        setTotalPages(response.data.last_page)
        setProducts(response.data.data)
      }

      setLoader(false)
    }

    if (hasValue(category)) {
      getProductsByCategory()
    }
    // fetch products
  }, [category, subcategory, currentPage, city])

  return (
    <div id="categories">
      <KonektoNavbar />
      <Container className="my-5">
        <div className="categories-path">
          {selectedCategory ? (
            <>
              <BiCategoryAlt
                className="categories-icon pointer"
                onClick={resetQueryParams}
              />
              <IoChevronForward />
              <span
                className="main-category-span"
                onClick={() => {
                  if (!hasValue(subcategory)) {
                    return
                  }

                  queryParams.delete('s')
                  queryParams.delete('page')
                  navigate(`?${queryParams.toString()}`)
                }}>
                {selectedCategory?.name}
              </span>
            </>
          ) : (
            <h4 className="fw-bold">{`Kategorije`}</h4>
          )}

          {selectedSubcategory ? (
            <>
              <IoChevronForward />
              <span>{selectedSubcategory?.name}</span>
            </>
          ) : null}
        </div>

        {hasValue(category) ? (
          <div className="d-flex flex-column flex-md-row mt-2">
            <div className="filters">
              {/** Subcategories */}
              <div className="group-wrapper filters-section shadow">
                <h5 className="mb-0">{`Potkategorije (${selectedCategory?.children?.length})`}</h5>

                <hr className="hr divider" />

                {selectedCategory?.children?.map(subcategory => {
                  const isActiveSubcategory =
                    subcategory.id == selectedSubcategory?.id

                  return (
                    <div
                      key={subcategory.id}
                      onClick={() => {
                        onCategoryClick(category, subcategory?.id)
                      }}
                      className={`subcategory-link ${isActiveSubcategory ? 'active-subcategory' : ''}`}>
                      {subcategory.name}
                    </div>
                  )
                })}
              </div>

              {/** Filters */}
              <SearchFilters city={city} />
            </div>

            <div className="products-list">
              <div className="group-wrapper shadow">
                <Row>
                  {products?.map(product => {
                    return (
                      <Col
                        className="mb-4"
                        key={product.id}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}>
                        <Product
                          product={product}
                          onCardClick={() => onProductCardClick(product)}
                        />
                      </Col>
                    )
                  })}
                </Row>

                {loader ? <Loader /> : null}
                {products?.length === 0 && !loader ? (
                  <p className="text-center mt-4 warning-text">{`Nema rezultata za odabranu kategoriju`}</p>
                ) : null}

                {totalPages > 1 ? (
                  <PaginationList
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                  />
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {/** Categories groups - shown if category is not selected */}
        {!hasValue(category) ? (
          <div className="mt-2">
            <Row>
              {categories?.map(category => {
                return (
                  <Col key={category.id} xs={12} sm={6} md={4} lg={3}>
                    <div
                      className="group-wrapper shadow"
                      onClick={() => onCategoryClick(category.id)}>
                      <h5 className="pointer underlined-on-hover">
                        {category.name}
                      </h5>

                      {category.children.map(childCategory => {
                        return (
                          <div
                            onClick={() =>
                              onCategoryClick(category.id, childCategory.id)
                            }
                            className="pointer underlined-on-hover"
                            key={childCategory.id}>
                            {childCategory.name}
                          </div>
                        )
                      })}
                    </div>
                  </Col>
                )
              })}
            </Row>
          </div>
        ) : null}
      </Container>
      <Footer />
    </div>
  )
}
